import { Buffer } from 'buffer'

export const state = () => {
	return {
		programs: [],
		ttl_programs: null,
		cache_duration_programs: 1800000, // 30 minutes
		cache_key_programs: '',
		program: {},
		ttl_program: null,
		cache_duration_program: 1800000,
		cache_key_program: '',
	}
}

function isInCachePrograms(state, companyId) {
	if (state.cache_key_programs !== buildCacheKeyPrograms(companyId)) {
		return false
	}
	if (state.ttl_programs === null || state.ttl_programs < Date.now()) {
		return false
	}
	return true
}

function buildCacheKeyPrograms(companyId) {
	return 'company:' + companyId + ':programs'
}

function isInCacheProgram(state, companyId, programId) {
	if (state.cache_key_program !== buildCacheKeyProgram(companyId, programId)) {
		return false
	}
	if (state.ttl_program === null || state.ttl_program < Date.now()) {
		return false
	}
	return true
}

function buildCacheKeyProgram(companyId, programId) {
	return `company:${companyId}:program:${programId}`
}

export const actions = {
	async uploadBanner(_, { companyId, extension, file_type: fileType, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/banner-upload-url/`, {
				params: { extension },
			})
			.then(async (res) => {
				await useNuxtApp().$api(res.signed_url, {
					method: 'PUT',
					headers: { 'Content-Type': fileType },
					body: Buffer.from(data.replace(/^data:image\/\w+;base64,/, ''), 'base64'),
				})
				return res.remote_url
			})
			.catch((err) => {
				throw err
			})
	},

	async count(_, { companyId, params = {} }) {
		params.limit = 1
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/`, { params })
			.then((res) => {
				return res.count
			})
	},

	async list({ state, commit }, { companyId }) {
		if (isInCachePrograms(state, companyId)) {
			return state.programs
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/`)
			.then((res) => {
				commit('setPrograms', { data: res, companyId })
				return res
			})
			.catch((err) => {
				commit('clearCachePrograms')
				throw err
			})
	},

	async filter(_, { companyId, filters }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/`, { params: filters })
			.then((res) => {
				return res
			})
			.catch((err) => {
				throw err
			})
	},

	async listDoneByCreator(_, { companyId, creatorId }) {
		return await useNuxtApp().$api(
			`api/company/${companyId}/creators/${creatorId}/campaigns/?creator_id=${creatorId}`,
		)
	},

	async retrieve({ state, commit }, { companyId, programId }) {
		if (isInCacheProgram(state, companyId, programId)) {
			return state.program
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/${programId}/`)
			.then((res) => {
				commit('setProgram', { data: res, companyId, programId })
				return res
			})
			.catch((err) => {
				commit('clearCacheProgram')
				throw err
			})
	},

	async exportGifting(_, { companyId, program }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/${program.id}/export-gifting-participant/`)
			.then((res) => {
				// create downloadable CSV file from data
				const url = window.URL.createObjectURL(new Blob([res]))
				const link = document.createElement('a')
				link.href = url
				link.setAttribute('download', `${program.tag}-participants.csv`)
				document.body.appendChild(link)
				link.click()
				link.remove()
			})
	},

	async enable({ dispatch, commit }, { companyId, program, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/${program.id}/`, {
				method: 'PATCH',
				body: data,
			})
			.then(async () => {
				commit('invalidateCachePrograms')
				return await dispatch('list', { companyId })
			})
			.catch((err) => {
				commit('invalidateCachePrograms')
				dispatch('list', { companyId })
				throw err
			})
	},

	async create({ commit }, { companyId, program }) {
		if (program.template === null) {
			delete program.template // programmes personnalisés: ne pas passer de champ template null
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/`, {
				method: 'POST',
				body: program,
			})
			.then((res) => {
				commit('clearCacheProgram')
				commit('clearCachePrograms')
				return res
			})
			.catch((err) => {
				commit('clearCacheProgram')
				commit('clearCachePrograms')
				throw err
			})
	},

	async partial_update({ dispatch, commit }, { companyId, program, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/${program.id}/`, {
				method: 'PATCH',
				body: data,
			})
			.then(() => {
				commit('invalidateCachePrograms')
				commit('invalidateCacheProgram')
			})
			.catch((err) => {
				dispatch('retrieve', { companyId, programId: program.id })
				commit('invalidateCachePrograms')
				commit('invalidateCacheProgram')
				throw err
			})
	},

	async update({ commit }, { companyId, program }) {
		if (program.template === null) {
			delete program.template // programmes personnalisés: ne pas passer de champ template null
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/campaigns/${program.id}/`, {
				method: 'PUT',
				body: program,
			})
			.then((res) => {
				commit('clearCacheProgram')
				commit('clearCachePrograms')
				return res
			})
			.catch((err) => {
				commit('clearCacheProgram')
				commit('clearCachePrograms')
				throw err
			})
	},

	async delete({ commit, dispatch }, { companyId, programId }) {
		return await useNuxtApp()
			.$api(`api/campaigns/${programId}/`, { method: 'DELETE' })
			.finally(() => {
				commit('invalidateCachePrograms')
				commit('clearCacheProgram')
				dispatch('list', { companyId })
			})
	},
}

export const mutations = {
	setPrograms(state, { data, companyId }) {
		state.programs = data
		state.ttl_programs = Date.now() + state.cache_duration_programs
		state.cache_key_programs = buildCacheKeyPrograms(companyId)
	},

	setProgram(state, { data, companyId, programId }) {
		state.program = data
		state.ttl_program = Date.now() + state.cache_duration_program
		state.cache_key_program = buildCacheKeyProgram(companyId, programId)
	},

	clearCachePrograms(state) {
		state.programs = []
		state.ttl_programs = null
		state.cache_key_programs = ''
	},

	clearCacheProgram(state) {
		state.program = {}
		state.ttl_program = null
		state.cache_key_program = ''
	},

	invalidateCachePrograms(state) {
		state.ttl_programs = null
	},

	invalidateCacheProgram(state) {
		state.ttl_program = null
	},
}
